import { ExpressionType_ClinicalFunction, ExpressionType_Deduction, ExpressionType_GeneralFunction, ExpressionType_GeneralScore, ExpressionType_InRange, ExpressionType_NormalRange, ExpressionType_ResolutionFunction, ExpressionType_Score } from '../../../_entity/index.js';
/**
 * Converts a database expression level to a JSON schema representation.
 * @param expression - The basic database expression to be converted.
 * @param dependencies all expression dependencies
 * @returns The converted expression in AI team schema format.
 */
export const convertExpression = (expression, dependencies) => {
    const convertedExpression = {
        name: expression.label,
        expression: expression.expression,
        terms: {},
    };
    if (expression_isClinicalFunction(expression, convertedExpression)) {
        setCommonProperties(expression, convertedExpression, 'clinical_function', 'boolean', dependencies);
    }
    else if (expression_isScore(expression, convertedExpression)) {
        setCommonProperties(expression, convertedExpression, 'score', 'number', dependencies);
    }
    else if (expression_isNormalRange(expression, convertedExpression)) {
        // @ts-ignore FIXME: type generation fails here!
        setCommonProperties(expression, convertedExpression, 'normal_range', 'tuple', dependencies);
    }
    else if (expression_isResolutionFunction(expression, convertedExpression))
        setCommonProperties(expression, convertedExpression, 'resolution_function', 'boolean', dependencies);
    else if (expression_isInRange(expression, convertedExpression))
        setCommonProperties(expression, convertedExpression, 'in_range', 'number', dependencies);
    else if (expression_isDeduction(expression, convertedExpression))
        setCommonProperties(expression, convertedExpression, 'deduction', 'value_mapping', dependencies);
    return convertedExpression;
};
/**
 * Sets common properties for different expression types.
 * @param expression - The database expression.
 * @param convertedExpression - The converted expression.
 * @param expressionType - The type of the expression.
 * @param outputType - The output type of the expression.
 * @param dependencies all expression dependencies
 */
const setCommonProperties = (expression, convertedExpression, expressionType, outputType, dependencies) => {
    convertedExpression.expression_type = expressionType;
    convertedExpression.output_type = outputType;
    convertedExpression.db_id = expression._id;
    convertedExpression._id = expression._id;
    // Assign the variable data to the expression
    Object.assign(convertedExpression, findAssociatedVariable(expression, dependencies));
    // Add expression description only if exists
    convertedExpression.description = expression.description?.replace(/[\\\n\t]/g, '') ?? '';
    // add the value mapping for deduction expression
    if (expressionType === ExpressionType_Deduction.dbKey)
        convertedExpression.value_mapping = expression.valueMapping;
};
/**
 * Finds the associated variable data for a given expression.
 * @param expression - The database expression.
 * @param dependencies all expression dependencies
 * @returns The associated variable data.
 * @throws {MUSTNeverHappenException} Throws an exception if the associated variable is not found.
 */
const findAssociatedVariable = (expression, dependencies) => {
    const associatedVar = dependencies.variables.find(_var => _var._id === expression.associatedVar);
    return {
        // TODO: redundant, should make sure with Dan REMOVE WHEN POSSIBLE
        snomed_id: 'MISSING!',
        associated_var_id: associatedVar?._id ?? null
    };
};
/**
 * Checks if the given expression is of type ClinicalFunction.
 * @param expression - The database expression.
 * @param convertedExpression - The converted expression.
 * @returns True if the expression is of type ClinicalFunction, otherwise false.
 */
const expression_isClinicalFunction = (expression, convertedExpression) => {
    return expression.expressionType === ExpressionType_ClinicalFunction.dbKey || expression.expressionType === ExpressionType_GeneralFunction.dbKey;
};
/**
 * Checks if the given expression is of type Score.
 * @param expression - The database expression.
 * @param convertedExpression - The converted expression.
 * @returns True if the expression is of type Score, otherwise false.
 */
const expression_isScore = (expression, convertedExpression) => {
    return expression.expressionType === ExpressionType_Score.dbKey || expression.expressionType === ExpressionType_GeneralScore.dbKey;
};
/**
 * Checks if the given expression is of type NormalRange.
 * @param expression - The database expression.
 * @param convertedExpression - The converted expression.
 * @returns True if the expression is of type NormalRange, otherwise false.
 */
const expression_isNormalRange = (expression, convertedExpression) => {
    return expression.expressionType === ExpressionType_NormalRange.dbKey;
};
/**
 * Checks if the given expression is of type NormalRange.
 * @param expression - The database expression.
 * @param convertedExpression - The converted expression.
 * @returns True if the expression is of type NormalRange, otherwise false.
 */
const expression_isResolutionFunction = (expression, convertedExpression) => {
    return expression.expressionType === ExpressionType_ResolutionFunction.dbKey;
};
/**
 * Checks if the given expression is of type InRange.
 * @param expression - The database expression.
 * @param convertedExpression - The converted expression.
 * @returns True if the expression is of type InRange, otherwise false.
 */
const expression_isInRange = (expression, convertedExpression) => {
    return expression.expressionType === ExpressionType_InRange.dbKey;
};
/**
 * Checks if the given expression is of type Deduction.
 * @param expression - The database expression.
 * @param convertedExpression - The converted expression.
 * @returns True if the expression is of type Deduction, otherwise false.
 */
const expression_isDeduction = (expression, convertedExpression) => {
    return expression.expressionType === ExpressionType_Deduction.dbKey;
};
//# sourceMappingURL=convertExpressionLevel.js.map